(ns common.sidebar.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/ListItemButton$default" :as ListItemButton]
            ["@mui/material/ListItemIcon$default" :as ListItemIcon]
            ["@mui/material/ListItemText$default" :as ListItemText]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.set :as set]
            [clojure.spec.alpha :as spec]
            [common.flags :refer [use-flags]]
            [common.spec]
            [common.util :refer [spread-obj]]
            [common.widgets.sidebar :as sidebar]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def DEFAULT-WIDTH 240)
(def SIDEBAR-TOGGLE-KEY "sidebar-toggle")

(spec/def ::link (spec/or :string string?
                          :keyword keyword?))

(spec/def ::href-fn any?)
(spec/def ::href-sub :reframe/sub-or-event)
(spec/def ::target string?)
(spec/def ::label string?)
(spec/def ::icon string?)

(spec/def ::navigation-item-props (spec/and (spec/keys :opt-un [::link ::href-fn ::href-sub ::target]
                                                       :req-un [::label ::icon])
                                            #(= 1 (count (set/intersection #{:link :href-fn :href-sub :on-click} (-> % keys set))))))

(def list-item-padding 2.5)

(rf/reg-sub ::default
  (fn []
    nil))

(defui navigation-link
  [{:keys [link]}]
  {:pre [(spec/assert ::navigation-item-props link)]}
  (let [{:keys [icon id label disabled
                link href-fn href-sub target on-click]
         :or {target "_self", disabled false}} link
        active-link (use-subscribe [:router/handler-name])
        link-context (use-subscribe [:router/path-params])
        href-data (use-subscribe (or href-sub [::default]))
        href* (cond
                (some? link) (r.easy/href link link-context)
                (some? href-fn) (href-fn link-context)
                (string? href-data) href-data
                (vector? href-data) (apply r.easy/href href-data))]
    ($ ListItem {:key label :disable-padding true}
       ($ ListItemButton
          {:href href*
           :onClick on-click
           :target target
           :disabled disabled
           :data-testid label
           :selected (= active-link (or link id))
           :sx #js {:justifyContent "initial"
                    :px list-item-padding
                    "&.Mui-selected" #js {:backgroundColor "primary.selected"
                                          "&:hover" #js {:backgroundColor "primary.200"}}}}
          ($ ListItemIcon
             {:sx #js {:minWidth 0
                       :mr 1.5}}
             ($ Icon icon))
          ($ ListItemText
             {:primary label
              :primaryTypographyProps #js {:noWrap true}
              :sx #js {:opacity 1}})))))

(defui navigation-list [{:keys [links]}]
  ($ List
     (for [link links]
       ($ navigation-link
          {:key link
           :link link}))))

(defui nav-header [{:keys [sx children]}]
  ($ Typography
     {:variant "h5"
      :textTransform "uppercase"
      :px list-item-padding
      :sx (spread-obj sx
                      #js {:color "text.secondary"})}
     children))

(defui nav-group [{:keys [children]}]
  ($ Box
     {:sx #js {:mt 2}}
     children))

(defui navigation-list-groups [{:keys [groups]}]
  ($ Stack
     {:direction "column"
      :flex 1}
     (doall
      (for [[group-name links] groups
            :let [links (if (map? links)
                          (:links links)
                          links)]]
        ($ nav-group {:key group-name}
           ($ nav-header group-name)
           ($ navigation-list {:links links}))))))

(defui sidebar [{:keys [page-links content paper-sx toggle-button?]
                 :or {toggle-button? true}}]
  (let [used-flags (into #{} (keep #(some-> % first :enable-flag)) (vals page-links))
        flag-values (use-flags used-flags)
        filtered-page-links (into {}
                                  (filter (fn [[_ [{:keys [enable-flag]}]]]
                                            (get flag-values enable-flag true))
                                          page-links))
        ;; for sake of safety
        *page-links (or filtered-page-links page-links)]
    ($ sidebar/sidebar
       {:toggle-key SIDEBAR-TOGGLE-KEY
        :width-key "sidebar-width"
        :default-width DEFAULT-WIDTH
        :min-width 50
        :drawer-anchor "left"
        :toggle-placement "right"
        :toggle-button? toggle-button?
        :paper-sx paper-sx}
       ($ Stack
          {:direction "column"
           :flex 1
           :sx #js {:overflowX "hidden"}}
          (if *page-links
            ($ navigation-list-groups
               {:groups *page-links})
            ;; New way to set the sidebar content.
            ;; Each page has full control of the sidebar contents.
            content)))))
